import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Link, useLocation } from 'react-router-dom';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);
  const navItems = [{
    name: 'Events',
    href: '/events',
    isRoute: true
  }, {
    name: 'Gallery',
    href: '/gallery',
    isRoute: true
  }, {
    name: 'Contact Us',
    href: '/contact',
    isRoute: true
  }, {
    name: 'Corporate Events',
    href: '/corporate-events',
    isRoute: true
  }, {
    name: 'Venues',
    href: '#venues',
    dropdown: [{
      name: 'Daytime',
      href: '/daytime',
      isRoute: true
    }, {
      name: 'Nightlife',
      href: '/nightlife',
      isRoute: true
    }, {
      name: 'After Hours',
      href: '/after-hours',
      isRoute: true
    }]
  }, {
    name: 'Reservations',
    href: '#reservations',
    dropdown: [{
      name: 'Bottle Service',
      href: '/bottle-service',
      isRoute: true
    }, {
      name: 'Food Reservation',
      href: 'https://www.ubereats.com',
      isRoute: false,
      isExternal: true
    }, {
      name: 'Hookah Booking',
      href: '/book-now',
      isRoute: true
    }]
  }];
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || location.pathname !== '/' ? 'bg-[#0B1014]/95 backdrop-blur-sm py-4 shadow-lg' : 'bg-transparent py-6'}`} data-id="element-336">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-337">
          {/* Logo */}
          <Link to="/" className="z-50" data-id="element-338">
            <img src="https://cdn.magicpatterns.com/uploads/kEvjjfeq9ckri24mEFMCRs/HOF-logo.png" alt="House of Flavors" className="h-24 w-auto" data-id="element-339" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-id="element-340">
            {navItems.map(item => <div key={item.name} className="relative group" onMouseEnter={() => setActiveDropdown(item.name)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-341">
                {item.isRoute ? <Link to={item.href} className="flex items-center text-lg font-medium text-white hover:text-[#DDA247] transition-colors tracking-wide" data-id="element-342">
                    {item.name}
                  </Link> : <a href={item.href} className="flex items-center text-lg font-medium text-white hover:text-[#DDA247] transition-colors tracking-wide" data-id="element-343">
                    {item.name}
                    {item.dropdown && <ChevronDown className="ml-1 w-4 h-4" data-id="element-344" />}
                  </a>}

                {/* Dropdown */}
                {item.dropdown && <AnimatePresence data-id="element-345">
                    {activeDropdown === item.name && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-48 bg-[#0B1014] border border-[#DDA247]/20 shadow-xl" data-id="element-346">
                        {item.dropdown.map(subItem => subItem.isRoute ? <Link key={subItem.name} to={subItem.href} className="block px-4 py-3 text-sm text-gray-300 hover:text-[#DDA247] hover:bg-white/5 transition-colors" data-id="element-347">
                              {subItem.name}
                            </Link> : <a key={subItem.name} href={subItem.href} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 text-sm text-gray-300 hover:text-[#DDA247] hover:bg-white/5 transition-colors" data-id="element-348">
                              {subItem.name}
                            </a>)}
                      </motion.div>}
                  </AnimatePresence>}
              </div>)}
          </nav>

          {/* Actions */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-349">
            <a href="#cart" className="text-white hover:text-[#DDA247] transition-colors relative" data-id="element-350">
              <ShoppingCart className="w-5 h-5" data-id="element-351" />
            </a>
            <Link to="/book-now" data-id="element-352">
              <Button variant="primary" size="sm" data-id="element-353">
                BOOK NOW
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden text-white z-[70] relative p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-354">
            {isMobileMenuOpen ? <X className="w-8 h-8" data-id="element-355" /> : <Menu className="w-8 h-8" data-id="element-356" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay - Outside header for proper fullscreen */}
      <AnimatePresence data-id="element-357">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-0 bg-[#0B1014] z-[60] flex flex-col justify-center items-center lg:hidden overflow-y-auto py-20" data-id="element-358">
            {/* Close button inside overlay */}
            <button className="absolute top-6 right-6 text-white p-2 z-10" onClick={() => setIsMobileMenuOpen(false)} data-id="element-359">
              <X className="w-8 h-8" data-id="element-360" />
            </button>

            <nav className="flex flex-col items-center space-y-6 px-6" data-id="element-361">
              {navItems.map(item => <div key={item.name} className="flex flex-col items-center space-y-4" data-id="element-362">
                  {item.isRoute ? <Link to={item.href} className="text-2xl font-medium text-white hover:text-[#DDA247] tracking-widest transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-363">
                      {item.name}
                    </Link> : <a href={item.href} className="text-2xl font-medium text-white hover:text-[#DDA247] tracking-widest transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-364">
                      {item.name}
                    </a>}

                  {/* Mobile Dropdown Items */}
                  {item.dropdown && <div className="flex flex-col items-center space-y-3" data-id="element-365">
                      {item.dropdown.map(subItem => subItem.isRoute ? <Link key={subItem.name} to={subItem.href} className="text-base font-normal text-gray-400 hover:text-[#DDA247] tracking-wide transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-366">
                            {subItem.name}
                          </Link> : <a key={subItem.name} href={subItem.href} target="_blank" rel="noopener noreferrer" className="text-base font-normal text-gray-400 hover:text-[#DDA247] tracking-wide transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-367">
                            {subItem.name}
                          </a>)}
                    </div>}
                </div>)}
              <Link to="/book-now" data-id="element-368">
                <Button className="mt-8" onClick={() => setIsMobileMenuOpen(false)} data-id="element-369">
                  BOOK NOW
                </Button>
              </Link>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </>;
}